import type { ChallengeWithStats, Participant } from '../types/challengeCard.types';
import { PENDING_1V1_NO_OTHER_LABEL } from '../constants/challenges.constants';
import { getUserName } from './challengeCardUi.helper';

/**
 * Normalize user ID for "is this the same user?" comparison only.
 * Use for comparing participant id / creatorId to currentUserId; do not use for lookup keys.
 */
export const normalizeUserIdForComparison = (id: string): string =>
  String(id).trim().toLowerCase();

/**
 * Build participants array from challenge data.
 * Includes ACCEPTED (from participantIds) and PENDING invitees (from participantStatuses) so the card shows the recipient when challenge is pending.
 * Display names and avatars come from challenge-embedded maps (no global users list).
 */
export const buildParticipants = (
  challenge: ChallengeWithStats & {
    participantEmailsById?: Record<string, string>;
    participantDisplayNamesById?: Record<string, string>;
    participantStatuses?: Array<{ userId: string; status: string }>;
  },
  currentUserId: string,
): Participant[] => {
  const participantEmailsById = (challenge as any).participantEmailsById || {};
  const participantDisplayNamesById = (challenge as any).participantDisplayNamesById || {};

  const ids = challenge.participantIds ?? [];
  const normalizedCurrent = normalizeUserIdForComparison(currentUserId);
  const seenNormalized = new Set<string>();
  const uniqueIds: string[] = [];
  for (const id of ids) {
    const key = String(id).trim();
    if (!key) continue;
    const norm = normalizeUserIdForComparison(id);
    if (seenNormalized.has(norm)) continue;
    seenNormalized.add(norm);
    uniqueIds.push(id);
  }

  const participantStatuses = challenge.participantStatuses ?? [];
  for (const p of participantStatuses) {
    const userId = String(p?.userId ?? '').trim();
    if (!userId) continue;
    const norm = normalizeUserIdForComparison(userId);
    if (seenNormalized.has(norm)) continue;
    const status = (p.status ?? '').toUpperCase();
    if (status === 'PENDING') {
      seenNormalized.add(norm);
      uniqueIds.push(userId);
    }
  }

  return uniqueIds.map((participantId: string) => {
    const isCurrentUser = normalizeUserIdForComparison(participantId) === normalizedCurrent;
    const participantName = isCurrentUser
      ? 'You'
      : getUserName(participantId, participantDisplayNamesById, participantEmailsById);

    return {
      id: participantId,
      name: participantName,
      avatar: isCurrentUser ? '👤' : '👥',
      isCurrentUser,
    };
  });
};

/**
 * Calculate display name for challenge card.
 * Never returns the current user's name as the "opponent" label (avoids "challenge against myself").
 * Uses challenge-embedded participantDisplayNamesById (no global users list).
 */
export const calculateDisplayName = (
  challenge: ChallengeWithStats & { participantDisplayNamesById?: Record<string, string> },
  currentUserId: string,
  participants: Participant[]
): string => {
  const normalizedCurrent = normalizeUserIdForComparison(currentUserId);
  const normalizedCreator = normalizeUserIdForComparison(challenge.creatorId);
  const isCreator = normalizedCreator === normalizedCurrent;

  let otherParticipant = participants.find(
    (p) =>
      normalizeUserIdForComparison(p.id) !== normalizedCreator &&
      normalizeUserIdForComparison(p.id) !== normalizedCurrent &&
      !p.isCurrentUser
  );

  if (!otherParticipant) {
    otherParticipant = participants.find(
      (p) => normalizeUserIdForComparison(p.id) !== normalizedCurrent && !p.isCurrentUser
    );
  }

  if (
    otherParticipant?.name &&
    normalizeUserIdForComparison(otherParticipant.id) !== normalizedCurrent
  ) {
    return otherParticipant.name;
  }
  if (challenge.groupId) return challenge.name || 'Group challenge';
  if (isCreator) return PENDING_1V1_NO_OTHER_LABEL;
  const displayNames = challenge.participantDisplayNamesById ?? {};
  const currentUserName = displayNames[currentUserId];
  if (challenge.name && currentUserName && challenge.name.trim() === currentUserName.trim()) {
    return 'Challenge';
  }
  return challenge.name || 'Challenge';
};

/**
 * Default selected opponent for group challenge participant switcher.
 * Finished: leader (winner). Active: other participant with highest points (tie-break: userId).
 */
export const getDefaultSelectedOpponentId = (
  challenge: ChallengeWithStats & { picks?: Array<{ userId: string; pointsEarned?: number | null }> },
  _currentUserId: string,
  otherParticipants: Participant[]
): string | null => {
  if (otherParticipants.length === 0) return null;
  if (otherParticipants.length === 1) return otherParticipants[0].id;

  const isFinished = String(challenge.status).toUpperCase() === 'COMPLETED';
  if (isFinished && challenge.leader?.userId) {
    const leaderInOthers = otherParticipants.some((p) => p.id === challenge.leader!.userId);
    if (leaderInOthers) return challenge.leader.userId;
  }

  const pointsByUserId: Record<string, number> = {};
  (challenge.picks ?? []).forEach((pick) => {
    const pts = pick.pointsEarned ?? 0;
    pointsByUserId[pick.userId] = (pointsByUserId[pick.userId] ?? 0) + pts;
  });

  const sorted = [...otherParticipants].sort((a, b) => {
    const pa = pointsByUserId[a.id] ?? 0;
    const pb = pointsByUserId[b.id] ?? 0;
    if (pb !== pa) return pb - pa;
    return a.id.localeCompare(b.id);
  });
  return sorted[0]?.id ?? null;
};

/**
 * Check if challenge can be cancelled by the current user.
 * Only the creator can cancel, and only when the challenge is still PENDING.
 */
export const canCancelChallenge = (
  challenge: ChallengeWithStats,
  currentUserId: string
): boolean => {
  const isCreator = challenge.creatorId === currentUserId;
  if (!isCreator) {
    return false;
  }
  const status = (challenge.status ?? '').toUpperCase();
  return status === 'PENDING';
};

